import React from "react";
import {
  Drawer,
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Typography,
  Divider,
  Tooltip,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  People as PeopleIcon,
  CalendarMonth as CalendarMonthIcon,
  Videocam as VideocamIcon,
  Shield as ShieldIcon,
  Api as ApiIcon,
  Logout as LogoutIcon,
  Settings as SettingsIcon,
  VideoCall as VideoCallIcon,
} from "@mui/icons-material";
import { FocusedLogo } from "../controls/FocusedLogo";
import { useAuthenticatedUserState } from "../../providers/AuthenticatedUserProvider";

export const DRAWER_WIDTH = 240;
export const DRAWER_COLLAPSED_WIDTH = 64;

export interface ScreenInfo {
  id: string;
  name: string;
  children?: ScreenInfo[];
}

interface SidebarProps {
  open: boolean;
  onToggle: () => void;
  screens: ScreenInfo[];
  activeScreenId: string;
  onNavigate: (screenId: string) => void;
}

// Map screen IDs to icons
const screenIcons: Record<string, React.ReactElement> = {
  userManagement: <PeopleIcon />,
  calendarManagement: <CalendarMonthIcon />,
  videoPlatform: <VideocamIcon />,
  newMeeting: <VideoCallIcon />,
  moderation: <ShieldIcon />,
  moderationConfig: <SettingsIcon />,
  apiRequests: <ApiIcon />,
};

export const Sidebar: React.FC<SidebarProps> = ({
  open,
  onToggle,
  screens,
  activeScreenId,
  onNavigate,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { signOut } = useAuthenticatedUserState();

  const handleNavigation = (screenId: string) => {
    onNavigate(screenId);
    // Auto-close on mobile after navigation
    if (isMobile) {
      onToggle();
    }
  };

  const drawerContent = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        backgroundColor: "#7357FF",
        color: "white",
        overflow: "hidden",
      }}
    >
      {/* Header with logo and collapse button */}
      <Box
        sx={{
          display: "flex",
          flexDirection: open ? "row" : "column",
          alignItems: "center",
          justifyContent: open ? "space-between" : "center",
          p: 2,
          minHeight: 64,
          gap: open ? 0 : 1,
        }}
      >
        {open ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <FocusedLogo sx={{ width: 32, height: 32 }} />
            <Typography
              variant="h6"
              sx={{
                fontFamily: "DM Sans, sans-serif",
                fontWeight: 400,
                letterSpacing: "0.05rem",
              }}
            >
              focused
            </Typography>
          </Box>
        ) : (
          !isMobile && <FocusedLogo sx={{ width: 32, height: 32 }} />
        )}
        {!isMobile && (
          <IconButton
            onClick={onToggle}
            size="small"
            sx={{
              color: "white",
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.1)",
              },
            }}
            aria-label={open ? "collapse sidebar" : "expand sidebar"}
          >
            {open ? <ChevronLeftIcon /> : <ChevronRightIcon />}
          </IconButton>
        )}
      </Box>

      <Divider sx={{ borderColor: "rgba(255, 255, 255, 0.2)" }} />

      {/* Navigation items */}
      <List sx={{ flex: 1, py: 1 }} role="navigation">
        {screens.map((screen) => {
          const isActive = activeScreenId === screen.id;
          const hasActiveChild = screen.children?.some((child) => activeScreenId === child.id);
          const icon = screenIcons[screen.id] || <ApiIcon />;

          return (
            <React.Fragment key={screen.id}>
              <ListItem disablePadding sx={{ display: "block" }}>
                <Tooltip
                  title={!open ? screen.name : ""}
                  placement="right"
                  arrow
                >
                  <ListItemButton
                    onClick={() => handleNavigation(screen.id)}
                    sx={{
                      minHeight: 48,
                      justifyContent: open ? "initial" : "center",
                      px: 2.5,
                      backgroundColor: isActive
                        ? "rgba(255, 255, 255, 0.2)"
                        : "transparent",
                      "&:hover": {
                        backgroundColor: isActive
                          ? "rgba(255, 255, 255, 0.25)"
                          : "rgba(255, 255, 255, 0.1)",
                      },
                      borderLeft: isActive || hasActiveChild ? "3px solid white" : "3px solid transparent",
                    }}
                    aria-current={isActive ? "page" : undefined}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: open ? 2 : "auto",
                        justifyContent: "center",
                        color: "white",
                      }}
                    >
                      {icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={screen.name}
                      sx={{
                        opacity: open ? 1 : 0,
                        transition: "opacity 0.2s",
                        "& .MuiTypography-root": {
                          fontWeight: isActive || hasActiveChild ? 600 : 400,
                          fontSize: "0.9rem",
                        },
                      }}
                    />
                  </ListItemButton>
                </Tooltip>
              </ListItem>
              {/* Render children (submenu items) */}
              {screen.children?.map((child) => {
                const isChildActive = activeScreenId === child.id;
                const hasActiveGrandchild = child.children?.some((gc) => activeScreenId === gc.id);
                const childIcon = screenIcons[child.id] || <ApiIcon />;

                return (
                  <React.Fragment key={child.id}>
                    <ListItem disablePadding sx={{ display: "block" }}>
                      <Tooltip
                        title={!open ? child.name : ""}
                        placement="right"
                        arrow
                      >
                        <ListItemButton
                          onClick={() => handleNavigation(child.id)}
                          sx={{
                            minHeight: 40,
                            justifyContent: open ? "initial" : "center",
                            px: 2.5,
                            pl: open ? 4 : 2.5,
                            backgroundColor: isChildActive
                              ? "rgba(255, 255, 255, 0.2)"
                              : "transparent",
                            "&:hover": {
                              backgroundColor: isChildActive
                                ? "rgba(255, 255, 255, 0.25)"
                                : "rgba(255, 255, 255, 0.1)",
                            },
                            borderLeft: isChildActive || hasActiveGrandchild ? "3px solid white" : "3px solid transparent",
                          }}
                          aria-current={isChildActive ? "page" : undefined}
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: 0,
                              mr: open ? 2 : "auto",
                              justifyContent: "center",
                              color: "white",
                              "& .MuiSvgIcon-root": {
                                fontSize: "1.2rem",
                              },
                            }}
                          >
                            {childIcon}
                          </ListItemIcon>
                          <ListItemText
                            primary={child.name}
                            sx={{
                              opacity: open ? 1 : 0,
                              transition: "opacity 0.2s",
                              "& .MuiTypography-root": {
                                fontWeight: isChildActive || hasActiveGrandchild ? 600 : 400,
                                fontSize: "0.85rem",
                              },
                            }}
                          />
                        </ListItemButton>
                      </Tooltip>
                    </ListItem>
                    {/* Render grandchildren (nested submenu items) */}
                    {child.children?.map((grandchild) => {
                      const isGrandchildActive = activeScreenId === grandchild.id;
                      const grandchildIcon = screenIcons[grandchild.id] || <ApiIcon />;

                      return (
                        <ListItem key={grandchild.id} disablePadding sx={{ display: "block" }}>
                          <Tooltip
                            title={!open ? grandchild.name : ""}
                            placement="right"
                            arrow
                          >
                            <ListItemButton
                              onClick={() => handleNavigation(grandchild.id)}
                              sx={{
                                minHeight: 36,
                                justifyContent: open ? "initial" : "center",
                                px: 2.5,
                                pl: open ? 6 : 2.5,
                                backgroundColor: isGrandchildActive
                                  ? "rgba(255, 255, 255, 0.2)"
                                  : "transparent",
                                "&:hover": {
                                  backgroundColor: isGrandchildActive
                                    ? "rgba(255, 255, 255, 0.25)"
                                    : "rgba(255, 255, 255, 0.1)",
                                },
                                borderLeft: isGrandchildActive ? "3px solid white" : "3px solid transparent",
                              }}
                              aria-current={isGrandchildActive ? "page" : undefined}
                            >
                              <ListItemIcon
                                sx={{
                                  minWidth: 0,
                                  mr: open ? 2 : "auto",
                                  justifyContent: "center",
                                  color: "white",
                                  "& .MuiSvgIcon-root": {
                                    fontSize: "1rem",
                                  },
                                }}
                              >
                                {grandchildIcon}
                              </ListItemIcon>
                              <ListItemText
                                primary={grandchild.name}
                                sx={{
                                  opacity: open ? 1 : 0,
                                  transition: "opacity 0.2s",
                                  "& .MuiTypography-root": {
                                    fontWeight: isGrandchildActive ? 600 : 400,
                                    fontSize: "0.8rem",
                                  },
                                }}
                              />
                            </ListItemButton>
                          </Tooltip>
                        </ListItem>
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </React.Fragment>
          );
        })}
      </List>

      <Divider sx={{ borderColor: "rgba(255, 255, 255, 0.2)" }} />

      {/* Sign out button at bottom */}
      <List sx={{ py: 1 }}>
        <ListItem disablePadding sx={{ display: "block" }}>
          <Tooltip title={!open ? "Sign Out" : ""} placement="right" arrow>
            <ListItemButton
              onClick={signOut}
              sx={{
                minHeight: 48,
                justifyContent: open ? "initial" : "center",
                px: 2.5,
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: open ? 2 : "auto",
                  justifyContent: "center",
                  color: "white",
                }}
              >
                <LogoutIcon />
              </ListItemIcon>
              <ListItemText
                primary="Sign Out"
                sx={{
                  opacity: open ? 1 : 0,
                  transition: "opacity 0.2s",
                  "& .MuiTypography-root": {
                    fontSize: "0.9rem",
                  },
                }}
              />
            </ListItemButton>
          </Tooltip>
        </ListItem>
      </List>
    </Box>
  );

  // Mobile: temporary drawer (overlay)
  if (isMobile) {
    return (
      <Drawer
        variant="temporary"
        open={open}
        onClose={onToggle}
        ModalProps={{
          keepMounted: true, // Better mobile performance
        }}
        sx={{
          "& .MuiDrawer-paper": {
            width: DRAWER_WIDTH,
            boxSizing: "border-box",
          },
        }}
      >
        {drawerContent}
      </Drawer>
    );
  }

  // Desktop: permanent drawer with collapse
  return (
    <Drawer
      variant="permanent"
      open={open}
      sx={{
        width: open ? DRAWER_WIDTH : DRAWER_COLLAPSED_WIDTH,
        flexShrink: 0,
        whiteSpace: "nowrap",
        boxSizing: "border-box",
        transition: theme.transitions.create("width", {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen,
        }),
        "& .MuiDrawer-paper": {
          width: open ? DRAWER_WIDTH : DRAWER_COLLAPSED_WIDTH,
          transition: theme.transitions.create("width", {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          overflowX: "hidden",
          boxSizing: "border-box",
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
};
